// ============================================================================
// REFERRAL SERVICE - Referral Tracking & Agent Management
// ============================================================================
// This service handles:
// - Referral code validation (via Cloud Function API)
// - $5 PKCash signup bonus for new users (1x playthrough)
// - $5 PKCash bonus for referrer (no playthrough)
// - Root agent tracking for rake distribution
// - Agent management and reporting
// ============================================================================

import { db } from './firebase';
import { API_BASE } from '../utils/apiBase';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  doc,
  Timestamp,
  increment,
  orderBy,
  limit,
  deleteField,
} from 'firebase/firestore';
import walletService from './walletService';

// ============================================================================
// CONFIGURATION
// ============================================================================

const REFERRAL_CONFIG = {
  // Signup bonus for new users who use a referral code
  SIGNUP_BONUS_PKCASH: 5.00,  // $5 PKCash (matches Cloud Function)
  
  // Bonus for the referrer when their referral signs up
  REFERRER_SIGNUP_BONUS: 5.00,  // $5 PKCash (matches Cloud Function)
  
  // Playthrough multiplier for signup bonus
  PLAYTHROUGH_MULTIPLIER: 1,  // 1x playthrough required
  
  // Agent tier rake percentages
  AGENT_TIERS: {
    standard: 0.10,  // 10%
    premium: 0.15,   // 15%
    vip: 0.20,       // 20%
  },
};

// ============================================================================
// INTERFACES
// ============================================================================

export interface Referral {
  id: string;
  referrerId: string;
  referredUserId: string;
  referredUserEmail?: string;
  rootAgentId?: string;  //  NEW: The root agent for this referral chain

  status: 'pending' | 'verified' | 'active';
  signupBonusPaid: boolean;
  rakeEarned: number;
  handsPlayed: number;
  createdAt: Date;
  verifiedAt?: Date;
}

export interface Agent {
  id: string;
  userId: string;
  displayName: string;
  email: string;
  referralCode: string;
  customCode?: string;  //  NEW: For custom codes like "BIGSTREAMER"

  active: boolean;
  tier: 'standard' | 'premium' | 'vip';
  rakePercent: number;
  totalReferrals: number;
  activeReferrals: number;
  totalRakeEarned: number;
  directReferrals: number;    //  NEW: Users who used agent's code directly

  indirectReferrals: number;  //  NEW: Users referred by agent's referrals

  // Manager layer: a manager is an agent with role 'manager' who earns an
  // override on everything the agents under them generate.
  role?: 'agent' | 'manager';
  managerId?: string;
  overrideRakePercent?: number;
  totalOverrideEarned?: number;
  totalPurchaseCommission?: number; // historical only: the program is rake-only now
  tournamentRakeEarned?: number;
  totalPaidOut?: number;

  createdAt: Date;
  updatedAt: Date;
}

export interface ReferralStats {
  totalReferrals: number;
  verifiedReferrals: number;
  activeReferrals: number;
  totalRakeEarned: number;
  pendingRake: number;
  thisMonthRake: number;
  referralList: Referral[];
}

export interface AgentDashboardData {
  agent: Agent;
  earnings: {
    total: number;
    thisMonth: number;
    lastMonth: number;
  };
  referrals: {
    direct: ReferralWithDetails[];
    indirect: ReferralWithDetails[];
  };
  payoutHistory: PayoutRecord[];
}

export interface ReferralWithDetails {
  referral: Referral;
  username: string;
  photoURL?: string;
  isDirect: boolean;
  referredVia?: string;  // For indirect: who referred them
  signupDate: Date;
  lastPlayedAt: Date | null;
  active: boolean;          // played in the last 30 days
  handsPlayed: number;
  tournamentsPlayed: number;
  /** Rake this player generated (what the house took on their play) */
  rakeGenerated: number;
  cashRakeGenerated: number;
  tournamentRakeGenerated: number;
  /** What the agent actually earned on this player */
  rakeEarned: number;
  cashRakeEarned: number;
  tournamentRakeEarned: number;
  /** True when rakeGenerated had to be estimated from older records */
  generatedEstimated: boolean;
}

export interface PayoutRecord {
  id: string;
  amount: number;
  date: Date;
  status: 'pending' | 'pending_review' | 'approved' | 'processing' | 'paid' | 'completed' | 'rejected';
  method?: string;
  reference?: string;
  description?: string;
  paidAt?: Date;
  adminNote?: string;
}

//  NEW: User search result for admin dashboard

export interface UserSearchResult {
  id: string;
  username: string;
  email: string;
  displayName?: string;
}

// ============================================================================
// REFERRAL SERVICE CLASS
// ============================================================================

class ReferralService {
  // ============================================================================
  // REFERRAL CODE GENERATION
  // ============================================================================

  /**
   * Generate referral code
   * Format: "PS" + 8 random alphanumeric chars
   */
  generateReferralCode(uid: string): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = 'PS';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }

  /**
   * Validate referral code format
   */
  isValidReferralCode(code: string): boolean {
    // Standard format: PS + 8 alphanumeric
    if (/^PS[A-Z0-9]{8}$/i.test(code)) {
      return true;
    }
    // Custom agent codes: 3-20 alphanumeric chars
    if (/^[A-Z0-9]{3,20}$/i.test(code)) {
      return true;
    }
    return false;
  }

  /**
   * Find user by referral code
   *  FIXED: Uses Cloud Function API to avoid permission issues for unauthenticated users

   */
  async findUserByReferralCode(code: string): Promise<string | null> {
    try {
      const normalizedCode = code.toUpperCase().trim();
      console.log(`🔍 [ReferralService] Validating referral code via API: "${normalizedCode}"`);
      
      // Call Cloud Function endpoint (no auth required)
      const apiBase = API_BASE;
      
      const response = await fetch(
        `${apiBase}/validateReferralCode?code=${encodeURIComponent(normalizedCode)}`
      );
      
      if (!response.ok) {
        console.error(`   ❌ API error: ${response.status}`);
        return null;
      }
      
      const result = await response.json();
      
      if (result.valid && result.referrerId) {
        console.log(`   ✅ Valid code! Referrer: ${result.referrerId.substring(0, 8)}...`);
        return result.referrerId;
      }
      
      console.log(`   ❌ Invalid referral code: "${normalizedCode}"`);
      return null;

    } catch (error) {
      console.error('❌ Error validating referral code:', error);
      return null;
    }
  }

  // ============================================================================
  //  ROOT AGENT RESOLUTION

  // ============================================================================

  /**
   * Check if a user is an active agent
   */
  async isAgent(userId: string): Promise<boolean> {
    try {
      const agentDoc = await getDoc(doc(db, 'agents', userId));
      return agentDoc.exists() && agentDoc.data().active === true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Resolve the root agent for a referral chain
   * 
   * Logic:
   * 1. If referrer is an agent → referrer is the rootAgentId
   * 2. If referrer has a rootAgentId → propagate that rootAgentId
   * 3. Otherwise → no agent in chain, return null
   */
  async resolveRootAgent(referrerId: string): Promise<string | null> {
    try {
      console.log(`🔍 [ReferralService] Resolving root agent for: ${referrerId.substring(0, 8)}`);

      // Check if direct referrer is an agent
      const isReferrerAgent = await this.isAgent(referrerId);
      
      if (isReferrerAgent) {
        console.log(`   ✅ Referrer IS an agent`);
        return referrerId;
      }

      // Check if referrer has a root agent
      const referrerDoc = await getDoc(doc(db, 'users', referrerId));
      
      if (referrerDoc.exists()) {
        const rootAgentId = referrerDoc.data().rootAgentId;
        if (rootAgentId) {
          console.log(`   ✅ Found root agent in chain: ${rootAgentId.substring(0, 8)}`);
          return rootAgentId;
        }
      }

      console.log(`   ℹ️ No agent in referral chain`);
      return null;

    } catch (error) {
      console.error('Error resolving root agent:', error);
      return null;
    }
  }

  /**
   * Get the root agent ID for a user (if any)
   */
  async getRootAgentId(userId: string): Promise<string | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return userDoc.data().rootAgentId || null;
      }
      return null;
    } catch (error) {
      console.error('Error getting root agent ID:', error);
      return null;
    }
  }

  // ============================================================================
  // REFERRAL TRACKING
  // ============================================================================

  /**
   * Track a new referral when user signs up with a code
   * 
   *  DEPRECATED: This function is no longer called during signup.

   * The Cloud Function /creditReferralBonus now handles:
   * - Creating the referral record
   * - Paying bonuses to both parties
   * - Updating agent stats
   * 
   * This function is kept for backwards compatibility and admin tools.
   * It does NOT pay bonuses (Cloud Function handles that).
   */
  async trackReferral(
    referrerId: string,
    referredUserId: string,
    referredUserEmail?: string
  ): Promise<string> {
    console.log(`🎯 [ReferralService] Tracking referral: ${referrerId.substring(0, 8)} → ${referredUserId.substring(0, 8)}`);
    console.log(`   ⚠️ Note: This is legacy code. Cloud Function handles bonus payments.`);

    try {
      // Check if referral already exists
      const existingQ = query(
        collection(db, 'referrals'),
        where('referredUserId', '==', referredUserId)
      );
      const existingSnapshot = await getDocs(existingQ);
      
      if (!existingSnapshot.empty) {
        console.log(`⚠️ User ${referredUserId.substring(0, 8)} already has a referrer`);
        return existingSnapshot.docs[0].id;
      }

      //  Resolve root agent

      const rootAgentId = await this.resolveRootAgent(referrerId);

      // Create referral record
      const referralData: any = {
        referrerId,
        referredUserId,
        referredUserEmail,
        status: 'pending',
        signupBonusPaid: false,  // Cloud Function will update this
        rakeEarned: 0,
        handsPlayed: 0,
        createdAt: Timestamp.now(),
      };

      //  Add rootAgentId if exists

      if (rootAgentId) {
        referralData.rootAgentId = rootAgentId;
      }

      // Deterministic id: the server books rake against referrals/{agent_player}
      // (rootAgentId first, else referrerId), so one player = one row.
      const referralDocId = `${rootAgentId || referrerId}_${referredUserId}`;
      const docRef = doc(db, 'referrals', referralDocId);
      await setDoc(docRef, referralData, { merge: true });

      console.log(`✅ Referral tracked: ${docRef.id}`);

      // Update user document with referrer ID and rootAgentId
      const userUpdates: any = {
        referrerId: referrerId,
        referredAt: Timestamp.now(),
      };

      if (rootAgentId) {
        userUpdates.rootAgentId = rootAgentId;
      }

      await updateDoc(doc(db, 'users', referredUserId), userUpdates);

      //  REMOVED: Don't pay bonuses here - Cloud Function handles this

      // await this.paySignupBonuses(referrerId, referredUserId, docRef.id);

      //  Update agent stats if there's a root agent

      if (rootAgentId) {
        const isDirect = referrerId === rootAgentId;
        await this.updateAgentReferralCount(rootAgentId, isDirect);
      }

      return docRef.id;
    } catch (error) {
      console.error('❌ Error tracking referral:', error);
      throw error;
    }
  }

  /**
   * Pay signup bonuses to both parties
   * 
   *  DEPRECATED: This function is no longer called.

   * The Cloud Function /creditReferralBonus handles all bonus payments.
   * Kept for reference only.
   */
  private async paySignupBonuses(
    referrerId: string,
    referredUserId: string,
    referralId: string
  ): Promise<void> {
    console.warn(`⚠️ paySignupBonuses is DEPRECATED - Cloud Function handles bonuses now`);
    // Do nothing - Cloud Function handles this
    return;
  }

  /**
   * Update referrer statistics
   */
  private async updateReferrerStats(referrerId: string): Promise<void> {
    try {
      const statsRef = doc(db, 'referralStats', referrerId);
      const statsDoc = await getDoc(statsRef);

      if (statsDoc.exists()) {
        await updateDoc(statsRef, {
          totalReferrals: increment(1),
          lastReferralDate: Timestamp.now(),
        });
      } else {
        await setDoc(statsRef, {
          userId: referrerId,
          totalReferrals: 1,
          totalRakeEarned: 0,
          lastReferralDate: Timestamp.now(),
          createdAt: Timestamp.now(),
        });
      }
    } catch (error) {
      console.error('Error updating referrer stats:', error);
    }
  }

  /**
   *  NEW: Update agent referral counts

   */
  private async updateAgentReferralCount(agentId: string, isDirect: boolean): Promise<void> {
    try {
      const agentRef = doc(db, 'agents', agentId);
      const updates: any = {
        totalReferrals: increment(1),
        updatedAt: Timestamp.now(),
      };

      if (isDirect) {
        updates.directReferrals = increment(1);
      } else {
        updates.indirectReferrals = increment(1);
      }

      await updateDoc(agentRef, updates);
    } catch (error) {
      console.error('Error updating agent referral count:', error);
    }
  }

  // ============================================================================
  //  USER SEARCH FOR ADMIN

  // ============================================================================

  /**
   *  NEW: Search users by username, email, or userId

   * Used by admin dashboard to find users to make agents
   */
  async searchUsers(searchTerm: string, limitCount: number = 10): Promise<UserSearchResult[]> {
    try {
      const search = searchTerm.toLowerCase().trim();
      if (!search || search.length < 2) {
        return [];
      }

      // Get all users and filter client-side (Firestore doesn't support LIKE queries)
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const results: UserSearchResult[] = [];

      usersSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const username = (data.username || '').toLowerCase();
        const email = (data.email || '').toLowerCase();
        const displayName = (data.displayName || '').toLowerCase();
        const id = docSnap.id.toLowerCase();

        // Match against any field
        if (
          username.includes(search) ||
          email.includes(search) ||
          displayName.includes(search) ||
          id.includes(search)
        ) {
          results.push({
            id: docSnap.id,
            username: data.username || data.displayName || 'Unknown',
            email: data.email || '',
            displayName: data.displayName,
          });
        }
      });

      // Sort by best match (exact match first)
      results.sort((a, b) => {
        const aExact = a.username.toLowerCase() === search || a.email.toLowerCase() === search;
        const bExact = b.username.toLowerCase() === search || b.email.toLowerCase() === search;
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;
        return 0;
      });

      return results.slice(0, limitCount);

    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  }

  /**
   *  NEW: Find user by userId, username, or email

   * Returns the user's Firebase UID if found
   */
  async findUserForAgent(identifier: string): Promise<{ userId: string; userData: any } | null> {
    try {
      const search = identifier.trim();
      
      // First, try direct userId lookup
      const directDoc = await getDoc(doc(db, 'users', search));
      if (directDoc.exists()) {
        return { userId: directDoc.id, userData: directDoc.data() };
      }

      // Search by username (case-insensitive)
      const usernameQuery = query(
        collection(db, 'users'),
        where('username', '==', search)
      );
      const usernameSnap = await getDocs(usernameQuery);
      if (!usernameSnap.empty) {
        const doc = usernameSnap.docs[0];
        return { userId: doc.id, userData: doc.data() };
      }

      // Search by username lowercase
      const usernameLowerQuery = query(
        collection(db, 'users'),
        where('username', '==', search.toLowerCase())
      );
      const usernameLowerSnap = await getDocs(usernameLowerQuery);
      if (!usernameLowerSnap.empty) {
        const doc = usernameLowerSnap.docs[0];
        return { userId: doc.id, userData: doc.data() };
      }

      // Search by email
      const emailQuery = query(
        collection(db, 'users'),
        where('email', '==', search)
      );
      const emailSnap = await getDocs(emailQuery);
      if (!emailSnap.empty) {
        const doc = emailSnap.docs[0];
        return { userId: doc.id, userData: doc.data() };
      }

      // Search by email lowercase
      const emailLowerQuery = query(
        collection(db, 'users'),
        where('email', '==', search.toLowerCase())
      );
      const emailLowerSnap = await getDocs(emailLowerQuery);
      if (!emailLowerSnap.empty) {
        const doc = emailLowerSnap.docs[0];
        return { userId: doc.id, userData: doc.data() };
      }

      return null;
    } catch (error) {
      console.error('Error finding user:', error);
      return null;
    }
  }

  // ============================================================================
  // AGENT MANAGEMENT
  // ============================================================================

  /**
   * Create or update an agent
   *  UPDATED: Now accepts username/email/userId and searches for user

   *  FIXED: Handles undefined customCode properly

   */
  async createAgent(
    userIdentifier: string,
    tier: 'standard' | 'premium' | 'vip' = 'standard',
    customCode?: string
  ): Promise<string> {
    try {
      //  NEW: Find user by identifier (userId, username, or email)

      const userResult = await this.findUserForAgent(userIdentifier);
      
      if (!userResult) {
        throw new Error(`User not found: ${userIdentifier}`);
      }

      const { userId, userData } = userResult;

      // Check if already an agent
      const existingAgent = await getDoc(doc(db, 'agents', userId));
      if (existingAgent.exists() && existingAgent.data().active) {
        throw new Error('User is already an active agent');
      }

      const rakePercent = REFERRAL_CONFIG.AGENT_TIERS[tier];

      //  FIXED: Build agent data without undefined fields

      const agentData: any = {
        userId,
        displayName: userData.displayName || userData.username || 'Unknown',
        email: userData.email || '',
        referralCode: userData.referralCode || this.generateReferralCode(userId),
        active: true,
        tier,
        rakePercent,
        totalReferrals: existingAgent.exists() ? existingAgent.data().totalReferrals || 0 : 0,
        activeReferrals: existingAgent.exists() ? existingAgent.data().activeReferrals || 0 : 0,
        totalRakeEarned: existingAgent.exists() ? existingAgent.data().totalRakeEarned || 0 : 0,
        directReferrals: existingAgent.exists() ? existingAgent.data().directReferrals || 0 : 0,
        indirectReferrals: existingAgent.exists() ? existingAgent.data().indirectReferrals || 0 : 0,
        createdAt: existingAgent.exists() ? existingAgent.data().createdAt : Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      //  FIXED: Only add customCode if it's provided and not empty

      if (customCode && customCode.trim()) {
        agentData.customCode = customCode.toUpperCase().trim();
      }
      // If reactivating and had a custom code, preserve it
      else if (existingAgent.exists() && existingAgent.data().customCode) {
        agentData.customCode = existingAgent.data().customCode;
      }

      await setDoc(doc(db, 'agents', userId), agentData);

      // Update user profile to mark as agent
      // NOTE: Only updating isAgent (updatedAt not allowed by Firestore rules for non-owner updates)
      await updateDoc(doc(db, 'users', userId), {
        isAgent: true,
      });

      console.log(`✅ Agent created: ${userId} (${tier})${customCode ? ` with code ${customCode}` : ''}`);
      return userId;

    } catch (error) {
      console.error('❌ Error creating agent:', error);
      throw error;
    }
  }

  /**
   * Update agent tier
   */
  async updateAgentTier(userId: string, tier: 'standard' | 'premium' | 'vip'): Promise<void> {
    try {
      const rakePercent = REFERRAL_CONFIG.AGENT_TIERS[tier];
      
      await updateDoc(doc(db, 'agents', userId), {
        tier,
        rakePercent,
        updatedAt: Timestamp.now(),
      });

      console.log(`✅ Agent tier updated: ${userId} → ${tier} (${rakePercent * 100}%)`);
    } catch (error) {
      console.error('❌ Error updating agent tier:', error);
      throw error;
    }
  }

  /**
   * Set custom code for agent
   */
  async setAgentCustomCode(userId: string, customCode: string): Promise<void> {
    try {
      const normalizedCode = customCode.toUpperCase().trim();
      
      // Check if code is already taken
      const existingQuery = query(
        collection(db, 'agents'),
        where('customCode', '==', normalizedCode)
      );
      const existing = await getDocs(existingQuery);
      
      if (!existing.empty && existing.docs[0].id !== userId) {
        throw new Error('Custom code already in use');
      }

      await updateDoc(doc(db, 'agents', userId), {
        customCode: normalizedCode,
        updatedAt: Timestamp.now(),
      });

      console.log(`✅ Custom code set: ${userId} → ${normalizedCode}`);
    } catch (error) {
      console.error('❌ Error setting custom code:', error);
      throw error;
    }
  }

  /**
   * Deactivate an agent
   */
  async deactivateAgent(userId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'agents', userId), {
        active: false,
        updatedAt: Timestamp.now(),
      });

      // NOTE: Only updating isAgent (updatedAt not allowed by Firestore rules for non-owner updates)
      await updateDoc(doc(db, 'users', userId), {
        isAgent: false,
      });

      console.log(`✅ Agent deactivated: ${userId}`);
    } catch (error) {
      console.error('❌ Error deactivating agent:', error);
      throw error;
    }
  }

  /**
   * Reactivate an agent
   */
  async reactivateAgent(userId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'agents', userId), {
        active: true,
        updatedAt: Timestamp.now(),
      });

      // NOTE: Only updating isAgent (updatedAt not allowed by Firestore rules for non-owner updates)
      await updateDoc(doc(db, 'users', userId), {
        isAgent: true,
      });

      console.log(`✅ Agent reactivated: ${userId}`);
    } catch (error) {
      console.error('❌ Error reactivating agent:', error);
      throw error;
    }
  }

  /**
   * Get all agents (for admin dashboard)
   */
  async getAllAgents(): Promise<Agent[]> {
    try {
      const q = query(
        collection(db, 'agents'),
        orderBy('totalRakeEarned', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const agents: Agent[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        agents.push({
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Agent);
      });

      return agents;

    } catch (error) {
      console.error('Error getting agents:', error);
      return [];
    }
  }

  /**
   * Get agent by ID
   */
  async getAgent(userId: string): Promise<Agent | null> {
    try {
      const agentDoc = await getDoc(doc(db, 'agents', userId));
      
      if (!agentDoc.exists()) {
        return null;
      }

      const data = agentDoc.data();
      return {
        id: agentDoc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Agent;

    } catch (error) {
      console.error('Error getting agent:', error);
      return null;
    }
  }

  // ============================================================================
  // AGENT DASHBOARD DATA
  // ============================================================================

  /**
   * Get complete dashboard data for an agent
   */
  async getAgentDashboardData(agentId: string): Promise<AgentDashboardData | null> {
    try {
      // Get agent info
      const agent = await this.getAgent(agentId);
      if (!agent) {
        return null;
      }

      // Get earnings data
      const earnings = await this.getAgentEarnings(agentId);

      // Every player attached to this agent, direct and indirect
      const { direct: directReferrals, indirect: indirectReferrals } = await this.getAgentReferrals(agentId, agent.rakePercent);

      // Get payout history
      const payoutHistory = await this.getAgentPayoutHistory(agentId);

      return {
        agent,
        earnings,
        referrals: {
          direct: directReferrals,
          indirect: indirectReferrals,
        },
        payoutHistory,
      };

    } catch (error) {
      console.error('Error getting agent dashboard data:', error);
      return null;
    }
  }

  /**
   * Get agent earnings summary
   *  FIX: Returns fields that match AgentDashboard expectations

   */
  private async getAgentEarnings(agentId: string): Promise<{
    total: number;
    thisMonth: number;
    lastMonth: number;
    //  NEW: Fields expected by AgentDashboard

    totalEarned?: number;
    cashGameEarned?: number;
    tournamentEarned?: number;
    handsRaked?: number;
    tournamentsRaked?: number;
    purchaseCommission?: number;
    overrideEarned?: number;
  }> {
    try {
      const agentDoc = await getDoc(doc(db, 'agents', agentId));
      const agentData = agentDoc.exists() ? agentDoc.data() : {};
      const total = agentData.totalRakeEarned || 0;
      const handsRaked = agentData.handsRaked || 0;
      const tournamentRakeEarned = agentData.tournamentRakeEarned || 0;
      const tournamentsRaked = agentData.tournamentsRaked || 0;
      const purchaseCommission = agentData.totalPurchaseCommission || 0;
      const overrideEarned = agentData.totalOverrideEarned || 0;

      // Calculate monthly earnings from transactions
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

      // Every kind of rake income: cash share, tournament share, manager override
      const q = query(
        collection(db, 'transactions'),
        where('userId', '==', agentId),
        where('type', 'in', ['rake_earned', 'tournament_rake_share', 'manager_override'])
      );

      const snapshot = await getDocs(q);
      let thisMonth = 0;
      let lastMonth = 0;

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const timestamp = data.timestamp?.toDate() || new Date();
        const amount = data.amount || 0;

        if (timestamp >= startOfMonth) {
          thisMonth += amount;
        } else if (timestamp >= startOfLastMonth && timestamp <= endOfLastMonth) {
          lastMonth += amount;
        }
      });

      //  FIX: Return all fields expected by AgentDashboard

      const cashGameEarned = total; // cash-game rake share
      const totalEarned = cashGameEarned + tournamentRakeEarned + purchaseCommission + overrideEarned;

      return {
        total: Math.round(total * 100) / 100,
        thisMonth: Math.round(thisMonth * 100) / 100,
        lastMonth: Math.round(lastMonth * 100) / 100,
        //  NEW: Fields for AgentDashboard

        totalEarned: Math.round(totalEarned * 100) / 100,
        cashGameEarned: Math.round(cashGameEarned * 100) / 100,
        tournamentEarned: Math.round(tournamentRakeEarned * 100) / 100,
        purchaseCommission: Math.round(purchaseCommission * 100) / 100,
        overrideEarned: Math.round(overrideEarned * 100) / 100,
        handsRaked,
        tournamentsRaked,
      };

    } catch (error) {
      console.error('Error getting agent earnings:', error);
      return { 
        total: 0, 
        thisMonth: 0, 
        lastMonth: 0,
        totalEarned: 0,
        cashGameEarned: 0,
        tournamentEarned: 0,
        handsRaked: 0,
        tournamentsRaked: 0,
      };
    }
  }

  /**
   * Every player attached to an agent, split into direct (signed up with the
   * agent's code) and indirect (brought in by one of the agent's players).
   *
   * Reads two plain equality queries (no composite index needed) and merges
   * by player, because older records can exist twice: a signup doc with the
   * real referrer, and a rake doc keyed `${agentId}_${playerId}` that the
   * server used to stamp with referrerId = agent. The real referrer wins.
   */
  private async getAgentReferrals(agentId: string, rakePercent: number): Promise<{ direct: ReferralWithDetails[]; indirect: ReferralWithDetails[] }> {
    try {
      const [byRoot, byReferrer] = await Promise.all([
        getDocs(query(collection(db, 'referrals'), where('rootAgentId', '==', agentId))),
        getDocs(query(collection(db, 'referrals'), where('referrerId', '==', agentId))),
      ]);
      const toDate = (v: any): Date | null => (v?.toDate ? v.toDate() : v instanceof Date ? v : null);
      const num = (v: any) => (typeof v === 'number' && isFinite(v) ? v : 0);

      // Merge every doc about the same player
      type Merged = { id: string; data: any; referrerId: string | null; createdAt: Date | null };
      const byPlayer = new Map<string, Merged>();
      for (const d of [...byRoot.docs, ...byReferrer.docs]) {
        const data = d.data();
        const pid = data.referredUserId;
        if (!pid || pid === agentId) continue;
        // A doc rooted at ANOTHER agent belongs to that agent's tree (this
        // user referred the player before becoming an agent themselves);
        // its rake is theirs, so it must not show here.
        if (data.rootAgentId && data.rootAgentId !== agentId) continue;
        const cur = byPlayer.get(pid);
        const realRef = data.referrerId && data.referrerId !== agentId ? data.referrerId : null;
        const created = toDate(data.createdAt);
        if (!cur) {
          byPlayer.set(pid, { id: d.id, data: { ...data }, referrerId: realRef || data.referrerId || null, createdAt: created });
          continue;
        }
        // Rake counters: take the larger value (the rake doc holds the real totals)
        for (const k of ['rakeEarned', 'cashRakeEarned', 'tournamentRakeEarned', 'rakeGenerated', 'cashRakeGenerated', 'tournamentRakeGenerated', 'handsPlayed', 'tournamentsPlayed']) {
          cur.data[k] = Math.max(num(cur.data[k]), num(data[k]));
        }
        const lp = toDate(data.lastPlayedAt);
        const curLp = toDate(cur.data.lastPlayedAt);
        if (lp && (!curLp || lp > curLp)) cur.data.lastPlayedAt = lp;
        if (realRef && cur.referrerId === agentId) cur.referrerId = realRef;
        if (!cur.referrerId && data.referrerId) cur.referrerId = data.referrerId;
        if (created && (!cur.createdAt || created < cur.createdAt)) cur.createdAt = created;
        if (!cur.data.referredUsername && data.referredUsername) cur.data.referredUsername = data.referredUsername;
      }

      // Player docs first; they also carry referrerId for players whose only
      // referral record is a rake doc (which never stores the referrer).
      const users = new Map<string, any>();
      const fetchUsers = async (ids: Iterable<string>) => Promise.all([...ids].map(async (uid) => {
        if (users.has(uid)) return;
        try { const u = await getDoc(doc(db, 'users', uid)); if (u.exists()) users.set(uid, u.data()); } catch { /* unreadable: fall back to Unknown */ }
      }));
      await fetchUsers(byPlayer.keys());
      byPlayer.forEach((m, pid) => {
        if (!m.referrerId) m.referrerId = users.get(pid)?.referrerId || null;
      });
      // Then the referrers, for the "via <who>" label
      const referrerIds = new Set<string>();
      byPlayer.forEach((m) => { if (m.referrerId && m.referrerId !== agentId) referrerIds.add(m.referrerId); });
      await fetchUsers(referrerIds);

      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
      const direct: ReferralWithDetails[] = [];
      const indirect: ReferralWithDetails[] = [];
      byPlayer.forEach((m, pid) => {
        const d = m.data;
        const u = users.get(pid);
        const isDirect = !m.referrerId || m.referrerId === agentId;
        const rakeEarned = num(d.rakeEarned);
        let rakeGenerated = num(d.rakeGenerated);
        let generatedEstimated = false;
        if (rakeGenerated === 0 && rakeEarned > 0 && rakePercent > 0) {
          // Records from before per-player generated tracking: back out the share
          rakeGenerated = rakeEarned / rakePercent;
          generatedEstimated = true;
        }
        const lastPlayedAt = toDate(d.lastPlayedAt);
        const signupDate = m.createdAt || toDate(u?.createdAt) || new Date(0);
        (isDirect ? direct : indirect).push({
          referral: { id: m.id, ...d, referrerId: m.referrerId || agentId, createdAt: signupDate, verifiedAt: toDate(d.verifiedAt) || undefined } as Referral,
          username: u?.username || d.referredUsername || 'Unknown',
          photoURL: u?.photoURL || u?.avatarUrl,
          isDirect,
          referredVia: isDirect ? undefined : (users.get(m.referrerId!)?.username || 'a player'),
          signupDate,
          lastPlayedAt,
          active: !!lastPlayedAt && lastPlayedAt.getTime() >= thirtyDaysAgo,
          handsPlayed: num(d.handsPlayed),
          tournamentsPlayed: num(d.tournamentsPlayed),
          rakeGenerated,
          cashRakeGenerated: num(d.cashRakeGenerated),
          tournamentRakeGenerated: num(d.tournamentRakeGenerated),
          rakeEarned,
          cashRakeEarned: num(d.cashRakeEarned),
          tournamentRakeEarned: num(d.tournamentRakeEarned),
          generatedEstimated,
        });
      });
      const newestFirst = (a: ReferralWithDetails, b: ReferralWithDetails) => b.signupDate.getTime() - a.signupDate.getTime();
      direct.sort(newestFirst); indirect.sort(newestFirst);
      return { direct, indirect };
    } catch (error) {
      console.error('Error getting agent referrals:', error);
      return { direct: [], indirect: [] };
    }
  }

  /**
   * Get agent payout history
   */
  private async getAgentPayoutHistory(agentId: string): Promise<PayoutRecord[]> {
    try {
      const q = query(
        collection(db, 'agentPayouts'),
        where('agentId', '==', agentId),
        // Requests are stamped requestedAt by /requestAgentPayout (the old
        // query ordered by a 'date' field nothing ever wrote, so history
        // was always empty).
        orderBy('requestedAt', 'desc'),
        limit(20)
      );

      const snapshot = await getDocs(q);
      const payouts: PayoutRecord[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        payouts.push({
          id: docSnap.id,
          amount: data.amount,
          date: data.requestedAt?.toDate() || data.createdAt?.toDate() || new Date(),
          status: data.status,
          method: data.payoutMethod || data.method,
          reference: data.payoutReference || data.stripeTransferId || undefined,
          paidAt: data.paidAt?.toDate?.(),
          adminNote: data.adminNote || undefined,
        });
      });

      return payouts;

    } catch (error) {
      console.error('Error getting payout history:', error);
      return [];
    }
  }

  // ============================================================================
  // STATISTICS
  // ============================================================================

  /**
   * Get referral stats for a user
   */
  async getReferralStats(userId: string): Promise<ReferralStats> {
    try {
      // Get referrals
      const q = query(
        collection(db, 'referrals'),
        where('referrerId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const referralList: Referral[] = [];
      let totalReferrals = 0;
      let verifiedReferrals = 0;
      let activeReferrals = 0;
      let totalRakeEarned = 0;

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        totalReferrals++;
        
        if (data.status === 'verified' || data.status === 'active') {
          verifiedReferrals++;
        }
        if (data.status === 'active') {
          activeReferrals++;
        }
        totalRakeEarned += data.rakeEarned || 0;

        referralList.push({
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          verifiedAt: data.verifiedAt?.toDate(),
        } as Referral);
      });

      // Get this month's rake
      const statsDoc = await getDoc(doc(db, 'referralStats', userId));
      const thisMonthRake = statsDoc.exists() ? statsDoc.data().thisMonthRake || 0 : 0;

      return {
        totalReferrals,
        verifiedReferrals,
        activeReferrals,
        totalRakeEarned,
        pendingRake: 0,
        thisMonthRake,
        referralList,
      };

    } catch (error) {
      console.error('Error getting referral stats:', error);
      return {
        totalReferrals: 0,
        verifiedReferrals: 0,
        activeReferrals: 0,
        totalRakeEarned: 0,
        pendingRake: 0,
        thisMonthRake: 0,
        referralList: [],
      };
    }
  }

  /**
   * Get top referrers (for leaderboard)
   */
  async getTopReferrers(limitCount: number = 10): Promise<{
    userId: string;
    totalReferrals: number;
    totalRakeEarned: number;
  }[]> {
    try {
      const q = query(
        collection(db, 'referralStats'),
        orderBy('totalRakeEarned', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      const results: {
        userId: string;
        totalReferrals: number;
        totalRakeEarned: number;
      }[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        results.push({
          userId: docSnap.id,
          totalReferrals: data.totalReferrals || 0,
          totalRakeEarned: data.totalRakeEarned || 0,
        });
      });

      return results;

    } catch (error) {
      console.error('Error getting top referrers:', error);
      return [];
    }
  }
}

// ============================================================================
// EXPORT
// ============================================================================

export const referralService = new ReferralService();
export default referralService;
export { REFERRAL_CONFIG };